import React from "react";
import { motion } from "framer-motion";
import {
  Instagram,
  Linkedin,
  Mail,
  Music2,
  BookOpen,
  Phone,
  MapPin,
  ArrowUpRight,
  Clock,
} from "lucide-react";
import { Overline } from "./Motifs";
import { SITE } from "../data/content";

const SOCIALS = [
  { id: "instagram", href: SITE.socials.instagram, label: "Instagram", icon: Instagram },
  { id: "linkedin", href: SITE.socials.linkedin, label: "LinkedIn", icon: Linkedin },
  { id: "medium", href: SITE.socials.medium, label: "Medium", icon: BookOpen },
  { id: "spotify", href: SITE.socials.spotify, label: "Spotify", icon: Music2 },
];

const REACH_REASONS = [
  "School & festival visits",
  "Workshop bookings",
  "Speaking engagements",
  "Collaborations & licensing",
  "Bulk book orders",
  "Just saying hello",
];

export default function Contact() {
  const mailtoSubject = encodeURIComponent("Hello Divya — from your website");
  const mailtoBody = encodeURIComponent(
    "Hi Divya,\n\nI came across your site and wanted to reach out about:\n\n[your message here]\n\nWarmly,\n"
  );
  const mailtoHref = `mailto:${SITE.email}?subject=${mailtoSubject}&body=${mailtoBody}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-[#F3EDE4]/60 border-t border-[#E5DFD3]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left — intro */}
          <div className="lg:col-span-5">
            <Overline>Get in touch</Overline>
            <h2 className="font-display text-5xl sm:text-6xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
              Let&apos;s tell a<br />
              <em className="italic font-light">story together.</em>
            </h2>
            <p className="text-[#5C6A8F] mt-6 leading-relaxed max-w-md">
              The easiest way to reach me is the old-fashioned way — an email
              or a phone call. I read every message myself and reply
              personally (sometimes slowly, always sincerely).
            </p>

            <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-2 max-w-md">
              {REACH_REASONS.map((r) => (
                <li
                  key={r}
                  className="text-sm text-[#1E2A4F] flex items-start gap-2"
                  data-testid={`contact-reason-${r.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                >
                  <span className="text-[#D95D39] mt-1">·</span> {r}
                </li>
              ))}
            </ul>

            <p className="mt-10 text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F]">
              Follow along
            </p>
            <ul className="mt-3 flex flex-wrap gap-3">
              {SOCIALS.map((s) => (
                <li key={s.id}>
                  <a
                    href={s.href}
                    target={s.href.startsWith("http") ? "_blank" : "_self"}
                    rel="noreferrer"
                    aria-label={s.label}
                    data-testid={`social-${s.id}`}
                    className="group flex items-center justify-center w-11 h-11 rounded-full border border-[#E5DFD3] text-[#1E2A4F] hover:bg-[#D95D39] hover:text-[#F9F6F0] hover:border-[#D95D39] transition-all duration-500"
                  >
                    <s.icon size={16} />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — direct line card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-testid="contact-direct-card"
            className="lg:col-span-7 relative rounded-2xl overflow-hidden bg-[#1E2A4F] text-[#F9F6F0] p-8 sm:p-12"
          >
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#D95D39]/30 blur-3xl pointer-events-none" />

            <span className="inline-flex items-center gap-2 relative">
              <Mail size={18} className="text-[#E76F51]" />
              <Overline className="!text-[#E76F51]">Direct line</Overline>
            </span>

            <h3 className="font-display text-4xl sm:text-5xl mt-4 leading-tight relative">
              Reach me{" "}
              <em className="italic font-light">directly.</em>
            </h3>

            <p className="text-[#F9F6F0]/70 mt-4 leading-relaxed max-w-md relative">
              Click below to open your email app with a draft ready, or call
              during India working hours.
            </p>

            <div className="mt-8 space-y-4 relative">
              <a
                href={mailtoHref}
                data-testid="contact-email"
                className="group flex items-center gap-4 p-4 sm:p-5 rounded-xl bg-[#F9F6F0]/5 hover:bg-[#D95D39] border border-[#F9F6F0]/10 transition-all duration-500"
              >
                <span className="w-12 h-12 rounded-full bg-[#D95D39] group-hover:bg-[#F9F6F0] group-hover:text-[#D95D39] inline-flex items-center justify-center text-[#F9F6F0] shrink-0 transition-colors duration-500">
                  <Mail size={18} />
                </span>
                <span className="flex-1">
                  <span className="block text-[10px] tracking-[0.24em] uppercase text-[#F9F6F0]/60 group-hover:text-[#F9F6F0]/80">
                    Email
                  </span>
                  <span className="block font-display text-xl sm:text-2xl mt-0.5 break-all">
                    {SITE.email}
                  </span>
                </span>
                <ArrowUpRight
                  size={18}
                  className="text-[#F9F6F0]/40 group-hover:text-[#F9F6F0] transition-colors shrink-0"
                />
              </a>

              <a
                href={SITE.phoneHref}
                data-testid="contact-phone"
                className="group flex items-center gap-4 p-4 sm:p-5 rounded-xl bg-[#F9F6F0]/5 hover:bg-[#D95D39] border border-[#F9F6F0]/10 transition-all duration-500"
              >
                <span className="w-12 h-12 rounded-full bg-[#D95D39] group-hover:bg-[#F9F6F0] group-hover:text-[#D95D39] inline-flex items-center justify-center text-[#F9F6F0] shrink-0 transition-colors duration-500">
                  <Phone size={18} />
                </span>
                <span className="flex-1">
                  <span className="block text-[10px] tracking-[0.24em] uppercase text-[#F9F6F0]/60 group-hover:text-[#F9F6F0]/80">
                    Phone / WhatsApp
                  </span>
                  <span className="block font-display text-xl sm:text-2xl mt-0.5">
                    {SITE.phone}
                  </span>
                </span>
                <ArrowUpRight
                  size={18}
                  className="text-[#F9F6F0]/40 group-hover:text-[#F9F6F0] transition-colors shrink-0"
                />
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#F9F6F0]/60 relative">
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={12} /> {SITE.location}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={12} /> IST 10am – 6pm
              </span>
            </div>

            <p className="mt-8 text-[11px] text-[#F9F6F0]/50 italic relative">
              I read everything personally. No autoresponders, no newsletters,
              no spam.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
